// Страница «Тюнинг» без разметки (PLAN.md, B3): разбор ответов /budgets,
// /schema и /simulate по ассетам, состояние ползунков в адресе и список
// «ассет · поле · было · стало» для переноса в инспектор.
//
// Сервер и CLI — единственные судьи того, что значат поля и допустимы ли
// значения. Здесь ничего не проверяется по смыслу: только форма данных.

export interface BudgetEntry {
  name: string;
  id: string | null;
  displayName: string | null;
}

export interface SchemaField {
  /** Адрес оверрайда: `Ассет._поле`, `Ассет._список[1]._поле`. */
  key: string;
  label: string;
  value: number;
  min: number;
  max: number;
  rangeSource: 'validator' | 'heuristic' | string;
  group: string;
  itemId: string | null;
}

export interface TuningSchema {
  budget: string;
  gitRevision: string | null;
  fields: SchemaField[];
}

export interface ItemDistribution {
  total: number;
  mean: number | null;
  p10: number | null;
  p50: number | null;
  p90: number | null;
  presentShare: number | null;
}

export interface ItemStat {
  itemId: string;
  placed: ItemDistribution;
  dropped: ItemDistribution;
}

/** Среднее количество предмета на забег по расстоянию комнаты от входа. */
export interface DepthProfile {
  maxDistance: number;
  roomsPerRun: number[];
  unreachableFinds: number;
  items: Array<{ itemId: string; perRun: number[] }>;
}

export interface DepthCell {
  itemId: string;
  distance: number;
  perRun: number;
  /** Доля предмета на этой глубине от всего предмета на уровне (0..1). */
  share: number;
}

export interface TuningResult {
  /** Бюджет, по которому посчитан ответ (source.budget): страница сверяет его с выбранным. */
  budget: string | null;
  configHash: string | null;
  runs: number;
  gitRevision: string | null;
  overrides: Record<string, number>;
  cacheHit: boolean;
  durationMs: number | null;
  /** Доли забегов: дефицит патронов держится без дропа и с ним; дроп ломает дефицит. */
  deficit: { respects: number | null; respectsWithDrops: number | null; dropsBreak: number };
  items: ItemStat[];
  emptyFinds: ItemDistribution | null;
  byDepth: DepthProfile | null;
}

export type Overrides = Record<string, number>;

const num = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const str = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

export function normalizeBudgets(payload: unknown): BudgetEntry[] {
  const list = (payload as { budgets?: unknown } | null)?.budgets;
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => (typeof entry === 'string' ? { name: entry } : (entry as Record<string, unknown>)))
    .filter((entry) => typeof entry.name === 'string')
    .map((entry) => ({ name: String(entry.name), id: str(entry.id), displayName: str(entry.displayName) }));
}

export function normalizeSchema(payload: unknown): TuningSchema {
  const root = (payload ?? {}) as Record<string, unknown>;
  const fields = Array.isArray(root.fields) ? (root.fields as Array<Record<string, unknown>>) : [];
  return {
    budget: String(root.budget ?? ''),
    gitRevision: str(root.gitRevision),
    fields: fields
      .filter((field) => typeof field.key === 'string' && num(field.value) !== null)
      .map((field) => {
        const value = num(field.value) as number;
        const min = num(field.min) ?? Math.min(0, value);
        const max = num(field.max) ?? Math.max(value * 4, min + 10);
        return {
          key: String(field.key),
          label: str(field.label) ?? String(field.key).split('.').slice(1).join('.'),
          value,
          min: Math.min(min, value),
          max: Math.max(max, value),
          rangeSource: String(field.rangeSource ?? 'heuristic'),
          group: str(field.group) ?? 'Прочее',
          itemId: typeof field.itemId === 'string' ? field.itemId : null,
        };
      }),
  };
}

function distribution(raw: unknown): ItemDistribution {
  const d = (raw ?? {}) as Record<string, unknown>;
  return {
    total: num(d.total) ?? 0,
    mean: num(d.mean),
    p10: num(d.p10),
    p50: num(d.p50),
    p90: num(d.p90),
    presentShare: num(d.presentShare),
  };
}

export function normalizeTuningResult(payload: unknown): TuningResult {
  const root = (payload ?? {}) as Record<string, unknown>;
  const summary = (root.summary ?? {}) as Record<string, unknown>;
  const boolean = (summary.boolean ?? {}) as Record<string, Record<string, unknown>>;
  const flags = Array.isArray(summary.flags) ? (summary.flags as Array<Record<string, unknown>>) : [];
  const source = (root.source ?? {}) as Record<string, unknown>;
  const items = (root.items ?? null) as Record<string, unknown> | null;
  const rawOverrides = (source.overrides ?? {}) as Record<string, unknown>;

  return {
    budget: str(source.budget),
    configHash: str(root.configHash),
    runs: num(root.runs) ?? num(summary.runs) ?? 0,
    gitRevision: str(source.gitRevision),
    overrides: Object.fromEntries(
      Object.entries(rawOverrides).filter(([, value]) => num(value) !== null).map(([key, value]) => [key, Number(value)]),
    ),
    cacheHit: root.cacheHit === true,
    durationMs: num(root.durationMs),
    deficit: {
      respects: num(boolean['economy.respectsAmmoDeficit']?.share),
      respectsWithDrops: num(boolean['economy.respectsAmmoDeficitWithDrops']?.share),
      dropsBreak: num(flags.find((flag) => flag.flag === 'drops_break_deficit')?.share) ?? 0,
    },
    items:
      items !== null && Array.isArray(items.items)
        ? (items.items as Array<Record<string, unknown>>).map((item) => ({
            itemId: String(item.itemId),
            placed: distribution(item.placed),
            dropped: distribution(item.dropped),
          }))
        : [],
    emptyFinds: items !== null && items.emptyFinds !== undefined ? distribution(items.emptyFinds) : null,
    byDepth: normalizeDepth(items?.byDepth),
  };
}

function normalizeDepth(raw: unknown): DepthProfile | null {
  if (raw === null || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const numbers = (list: unknown) => (Array.isArray(list) ? list.map((v) => num(v) ?? 0) : []);
  const items = Array.isArray(d.items) ? (d.items as Array<Record<string, unknown>>) : [];
  return {
    maxDistance: num(d.maxDistance) ?? 0,
    roomsPerRun: numbers(d.roomsPerRun),
    unreachableFinds: num(d.unreachableFinds) ?? 0,
    items: items.map((item) => ({ itemId: String(item.itemId), perRun: numbers(item.perRun) })),
  };
}

/**
 * Клетки тепловой карты «предмет × глубина». Цвет — доля предмета на этой
 * глубине от всего этого предмета: у талонов десятки на забег, у аптечек
 * десятые, и общая шкала показала бы только талоны.
 */
export function depthCells(profile: DepthProfile): DepthCell[] {
  return profile.items.flatMap((item) => {
    const total = item.perRun.reduce((sum, value) => sum + value, 0);
    return item.perRun.map((perRun, distance) => ({
      itemId: item.itemId,
      distance,
      perRun,
      share: total > 0 ? perRun / total : 0,
    }));
  });
}

// --- состояние в адресе ------------------------------------------------------

/** base64url без хвостовых `=`: строка уходит в адрес как есть. */
export function encodeOverrides(overrides: Overrides): string {
  const entries = Object.entries(overrides).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  if (entries.length === 0) return '';
  const bytes = new TextEncoder().encode(JSON.stringify(Object.fromEntries(entries)));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Разбор `?o=`; битая строка — пустые оверрайды, а не падение страницы. */
export function decodeOverrides(encoded: string | null): Overrides {
  if (!encoded) return {};
  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (encoded.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([key, value]) => typeof key === 'string' && typeof value === 'number' && Number.isSafeInteger(value),
      ),
    ) as Overrides;
  } catch {
    return {};
  }
}

/**
 * Оставить только то, что есть в схеме и отличается от ассета: ссылка
 * со старой ревизии не должна тащить поля, которых больше нет, а «подкрутил
 * обратно до исходного» — это не оверрайд.
 */
export function cleanOverrides(fields: readonly SchemaField[], overrides: Overrides): Overrides {
  const base = new Map(fields.map((field) => [field.key, field.value]));
  return Object.fromEntries(
    Object.entries(overrides).filter(([key, value]) => base.has(key) && base.get(key) !== value),
  );
}

export function withOverride(overrides: Overrides, field: SchemaField, value: number): Overrides {
  const next = { ...overrides };
  if (value === field.value) delete next[field.key];
  else next[field.key] = value;
  return next;
}

// --- группы и подписи ----------------------------------------------------------

export function groupFields(fields: readonly SchemaField[]): Array<{ group: string; fields: SchemaField[] }> {
  const groups = new Map<string, SchemaField[]>();
  for (const field of fields) {
    const list = groups.get(field.group);
    if (list) list.push(field);
    else groups.set(field.group, [field]);
  }
  return [...groups].map(([group, list]) => ({ group, fields: list }));
}

/**
 * Подписи — текст интерфейса, а не данные игры (CONTRACT.md, `schema`):
 * CLI отдаёт имя поля, человеческое название живёт здесь. Нет в словаре —
 * показываем имя поля, как его видно в инспекторе.
 */
const LABELS: Record<string, string> = {
  _minRooms: 'Комнат минимум',
  _maxRooms: 'Комнат максимум',
  _ammoBudgetPermille: 'Доля патронов, ‰',
  _averageShotsPerEnemy: 'Выстрелов на врага',
  _minEnemies: 'Врагов минимум',
  _maxEnemies: 'Врагов максимум',
  _enemiesPerHundredCells: 'Врагов на 100 клеток',
  _dropChancePercent: 'Шанс дропа, %',
  _weight: 'Вес строки',
  _min: 'Минимум',
  _max: 'Максимум',
  _stackMin: 'Пачка от',
  _stackMax: 'Пачка до',
};

/**
 * Значения-заглушки, у которых в игре особый смысл (Tooltip поля):
 * у варианта врага шанс дропа −1 — «как у архетипа EN_Zombie»
 * (EnemyVariantDef._dropChancePercent).
 */
export function specialValue(field: SchemaField, value: number): string | null {
  if (value === -1 && field.min === -1 && field.key.endsWith('._dropChancePercent')) return 'как у архетипа';
  return null;
}

export function splitKey(key: string): { asset: string; path: string } {
  const dot = key.indexOf('.');
  return dot < 0 ? { asset: key, path: '' } : { asset: key.slice(0, dot), path: key.slice(dot + 1) };
}

export function fieldTitle(field: SchemaField): string {
  const { path } = splitKey(field.key);
  const last = path.split('.').at(-1) ?? path;
  const name = last.replace(/\[\d+\]$/, '');
  // Пачка бывает двух механик: у строки бюджета (_lootRows) — на полу уровня,
  // у строки таблицы (_entries) — только с трупа (дроп-таблица врага, CONTRACT.md).
  const stackWhere = /^_stack(Min|Max)$/.test(name) ? (path.includes('_entries[') ? ' с трупа' : ' на полу') : '';
  const known = LABELS[name] === undefined ? undefined : LABELS[name]!.replace(/^Пачка/, `Пачка${stackWhere}`);
  const index = /\[(\d+)\]/.exec(path)?.[1];
  const base = known ?? field.label;
  const item = field.itemId !== null ? (field.itemId === '' ? ' · «ничего»' : ` · ${field.itemId}`) : '';
  return index !== undefined && known !== undefined ? `${base} [${index}]${item}` : `${base}${item}`;
}

// --- было/стало ----------------------------------------------------------------

export interface ChangeRow {
  key: string;
  asset: string;
  field: string;
  was: number;
  now: number;
}

/** Только отличающиеся от ассета поля, в порядке схемы. */
export function changedFields(fields: readonly SchemaField[], overrides: Overrides): ChangeRow[] {
  return fields
    .filter((field) => overrides[field.key] !== undefined && overrides[field.key] !== field.value)
    .map((field) => {
      const { asset, path } = splitKey(field.key);
      return { key: field.key, asset, field: path, was: field.value, now: overrides[field.key] as number };
    });
}

/** Markdown-таблица для чата/задачи: что перенести в инспектор. */
export function changesMarkdown(rows: readonly ChangeRow[], budget: string, gitRevision: string | null): string {
  const header = `Тюнинг ${budget}` + (gitRevision ? ` @ ${gitRevision}` : '');
  if (rows.length === 0) return `${header}: изменений нет.`;
  return [
    header,
    '',
    '| Ассет | Поле | Было | Стало |',
    '|---|---|---:|---:|',
    ...rows.map((row) => `| ${row.asset} | \`${row.field}\` | ${row.was} | ${row.now} |`),
  ].join('\n');
}
